import React, { useState, useEffect } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { Phone, ArrowRight, LifeBuoy } from 'lucide-react';
import { Button } from './ui/button';
import {
  NavigationMenu, NavigationMenuList, NavigationMenuItem,
  NavigationMenuTrigger, NavigationMenuContent,
} from './ui/navigation-menu';
import { NAV_CATEGORIES } from '../mock';
import { useLanguage } from '../i18n/LanguageContext';

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [navValue, setNavValue] = useState('');
  const navigate = useNavigate();
  const { t } = useLanguage();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const linkClass = ({ isActive }) =>
    `inline-flex items-center h-9 shrink-0 whitespace-nowrap text-sm font-medium leading-none transition-colors hover:text-[#14A098] ${isActive ? 'text-[#14A098]' : 'text-[#0f2540]'}`;

  const servicesTriggerClass =
    'inline-flex h-9 shrink-0 items-center whitespace-nowrap rounded-md bg-transparent px-2.5 py-0 text-sm font-medium leading-none text-[#0f2540] shadow-none hover:bg-teal-50/60 hover:text-[#14A098] focus:bg-teal-50/60 data-[state=open]:bg-teal-50/60 data-[state=open]:text-[#14A098] data-[state=open]:hover:bg-teal-50/60';

  const handleCategoryClick = (categoryCode) => {
    setNavValue('');
    navigate(`/services/${categoryCode.toLowerCase()}`);
  };

  const catLabel = (cat) => t(`category.${cat.category}`, cat.label);
  const catDesc = (cat) => t(`category.${cat.category}_desc`, cat.desc);

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-white'}`}>
      <nav className="w-full max-w-[1920px] mx-auto px-3 sm:px-4 md:px-6 lg:px-8 h-20 flex items-center justify-between gap-2 sm:gap-3 md:gap-4 min-w-0">
        <Link to="/" className="flex items-center gap-2 group shrink-0">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#14A098] to-[#5eead4] flex items-center justify-center shadow-md group-hover:scale-105 transition-transform">
            <span className="text-white font-bold text-xl">S</span>
          </div>
          <div className="leading-tight">
            <div className="font-extrabold text-[#0f2540] tracking-tight text-lg">SOLVE <span className="text-[#14A098]">TAX</span></div>
            <div className="text-[10px] text-gray-500 uppercase tracking-wider">{t('nav.tagline', 'Your Finances, Our Priority')}</div>
          </div>
        </Link>

        <div className="hidden lg:flex flex-wrap items-center justify-center gap-x-3 gap-y-1 xl:gap-x-4 min-w-0 flex-1 px-1">
          <NavLink to="/" end className={linkClass}>{t('nav.home', 'Home')}</NavLink>
          <NavLink to="/about" className={linkClass}>{t('nav.about', 'About')}</NavLink>

          <NavigationMenu value={navValue} onValueChange={setNavValue} className="max-w-max flex-none">
            <NavigationMenuList className="justify-start space-x-0">
              <NavigationMenuItem value="services">
                <NavigationMenuTrigger className={servicesTriggerClass}>
                  {t('nav.all_services', 'All Services')}
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <div className="p-4 w-[680px] max-w-[90vw] grid grid-cols-2 gap-2">
                    {NAV_CATEGORIES.map((cat) => {
                      const FirstIcon = cat.services[0]?.icon;
                      return (
                        <button
                          key={cat.category}
                          type="button"
                          onClick={() => handleCategoryClick(cat.category)}
                          className="text-left p-4 rounded-xl hover:bg-teal-50 transition-colors group flex items-start gap-3"
                        >
                          <div className="w-10 h-10 rounded-lg bg-teal-50 group-hover:bg-white flex items-center justify-center shrink-0 transition-colors">
                            {FirstIcon && <FirstIcon className="w-5 h-5 text-[#14A098]" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-bold text-[#0f2540] group-hover:text-[#14A098]">{catLabel(cat)}</div>
                            <div className="text-xs text-gray-500 leading-snug mt-0.5">{catDesc(cat)}</div>
                            <div className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-semibold text-[#14A098]">
                              {t('nav.view_n_services', 'View {n} services', { n: cat.services.length })} <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </NavigationMenuContent>
              </NavigationMenuItem>
            </NavigationMenuList>
          </NavigationMenu>

          <NavLink to="/contact" className={linkClass}>{t('nav.contact', 'Contact')}</NavLink>
          <NavLink to="/referral" className={linkClass}>{t('nav.referral', 'Referral')}</NavLink>
          <NavLink to="/privacy" className={linkClass}>{t('nav.privacy', 'Privacy')}</NavLink>
        </div>

        <div className="hidden lg:flex items-center justify-end gap-1.5 xl:gap-2 shrink-0 min-w-0">
          <a href="tel:+919346004430" className="shrink-0">
            <Button className="h-9 xl:h-10 px-2.5 xl:px-4 text-[12px] xl:text-sm bg-[#14A098] hover:bg-[#0e7a74] text-white rounded-full border-2 border-[#14A098] shadow-md shadow-[#14A098]/30 whitespace-nowrap">
              <Phone className="w-3.5 h-3.5 xl:w-4 xl:h-4 mr-1 xl:mr-2 shrink-0" /> {t('nav.call_now', 'Call Now')}
            </Button>
          </a>
          <Button onClick={() => navigate('/contact')} variant="outline" className="h-9 xl:h-10 px-2.5 xl:px-4 text-[12px] xl:text-sm border-2 border-[#14A098] text-[#14A098] hover:bg-[#14A098] hover:text-white rounded-full whitespace-nowrap shadow-sm shrink-0">
            <LifeBuoy className="w-3.5 h-3.5 xl:w-4 xl:h-4 mr-1 xl:mr-2 shrink-0" /> {t('nav.customer_support', 'Customer Support')}
          </Button>
        </div>

        <div className="lg:hidden flex items-center gap-2 shrink-0 ml-auto">
          <a
            href="tel:+919346004430"
            className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#14A098] text-white shadow-sm shadow-[#14A098]/30 hover:bg-[#0e7a74] transition-colors"
            aria-label={t('nav.mobile_call', 'Call')}
          >
            <Phone className="w-4 h-4" />
          </a>
          <Button
            onClick={() => navigate('/contact')}
            variant="outline"
            className="h-9 px-3 text-xs border-2 border-[#14A098] text-[#14A098] rounded-full hover:bg-[#14A098] hover:text-white"
          >
            <LifeBuoy className="w-3.5 h-3.5 mr-1.5" />
            {t('nav.mobile_support', 'Support')}
          </Button>
        </div>
      </nav>
    </header>
  );
};

export default Navbar;
